/* eslint-disable max-lines -- not split yet: TimeInputSection and the direction hint helper could move out */
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  type BookingRecordDetailsCustomer,
  type BookingUpdateOfferPublicRecord,
  BookingUpdateOfferStatus,
  BookingUpdateRequestWidenDirection
} from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import {
  ApiErrorMessageInternal,
  NoddiBasicCard,
  NoddiButton,
  NoddiInfoBanner,
  NoddiSelect,
  noddiToast
} from 'noddi-ui-web';
import { formatWholeMinutesAsTimeString } from 'noddi-util';
import { useState } from 'react';
import {
  buildWidenTimeSelectOptions,
  clampHmToRange,
  getAcceptWidenUserFacingError,
  getSalesItemIds,
  getWidenDateForApi,
  getWidenFeasibleDiscountTiers,
  getWidenSelectionState,
  getWidenUiBounds,
  localWallTimeOnServiceDateToUtcIso,
  WIDEN_TIME_SELECT_STEP_MINUTES,
  type WidenTimeSelectOption
} from '../../../utils/bookingUpdateOfferSectionUtils';
import { getCommonTranslations } from '../../../utils/commonTranslations';
import { DiscountTiersList } from '../DiscountTiersList';

interface Props {
  booking: BookingRecordDetailsCustomer;
  offer: BookingUpdateOfferPublicRecord;
  onBack: () => void;
  onRequireLogin: (action: 'widen' | 'reschedule' | 'decline') => boolean;
  onSuccess: () => Promise<void>;
}

const getWidenDirectionCustomerHint = (widenDirection: BookingUpdateOfferPublicRecord['widenDirection']) => {
  switch (widenDirection) {
    case BookingUpdateRequestWidenDirection.LATER:
      return t`We mainly need more flexibility later in the day.`;
    case BookingUpdateRequestWidenDirection.EARLIER:
      return t`We mainly need more flexibility earlier in the day.`;
    case BookingUpdateRequestWidenDirection.BOTH:
      return t`You can widen your window earlier in the day, later in the day, or both.`;
    default:
      return null;
  }
};

const TimeInputSection = ({
  currentTime,
  fallbackLabel,
  inputLabel,
  isDisabled,
  isEditorVisible,
  onChange,
  options,
  value
}: {
  currentTime: string;
  fallbackLabel: string;
  inputLabel: string;
  isDisabled: boolean;
  isEditorVisible: boolean;
  onChange: (value: string) => void;
  options: WidenTimeSelectOption[];
  value: string;
}) => {
  if (!isEditorVisible) {
    return (
      <div>
        <p className='text-xs text-muted-foreground'>{inputLabel}</p>
        <p className='text-sm font-medium tabular-nums'>{currentTime}</p>
      </div>
    );
  }

  return (
    <>
      <NoddiSelect
        label={inputLabel}
        value={value}
        options={options}
        onChange={(newValue) => {
          if (newValue != null) {
            onChange(newValue);
          }
        }}
        disabled={isDisabled}
        searchable={false}
        translation={{ ...getCommonTranslations().select, placeholder: t`Select` }}
      />
      <p className='text-xs text-muted-foreground'>{fallbackLabel}</p>
    </>
  );
};

export const BookingUpdateOfferWidenFlow = ({ booking, offer, onBack, onRequireLogin, onSuccess }: Props) => {
  const [widenStartTime, setWidenStartTime] = useState('');
  const [widenEndTime, setWidenEndTime] = useState('');

  const { mutate: acceptWiden, isPending: isAcceptPending } = noddiAsync.usePost({
    type: URLKeys.postBookingUpdateOfferAcceptWiden,
    queryConfig: {
      onSuccess,
      onError: (error) => {
        noddiToast.error(getAcceptWidenUserFacingError(error) ?? t`Something went wrong. Please try again.`);
      }
    }
  });

  const salesItemIds = getSalesItemIds(booking);
  const widenDateForApi = getWidenDateForApi({ offer, booking });
  const windowsQueryEnabled =
    widenDateForApi.length > 0 &&
    offer.widenOffered &&
    !offer.expired &&
    offer.status === BookingUpdateOfferStatus.PENDING &&
    salesItemIds.length > 0;

  const {
    data: availableWindows,
    error: availableWindowsError,
    isLoading: isAvailableWindowsLoading
  } = noddiAsync.useGet({
    type: URLKeys.getAvailableBookingTimeWindowsByDate,
    input: {
      addressId: booking.address.id,
      brandDomain: booking.customerBrand.domain,
      fromDate: widenDateForApi,
      toDate: widenDateForApi,
      selectedSalesItemIds: salesItemIds
    },
    queryConfig: { enabled: windowsQueryEnabled }
  });

  const widenUiBounds = getWidenUiBounds({ offer, booking });
  const widenFeasibleDiscountTiers = getWidenFeasibleDiscountTiers({ offer, widenUiBounds });
  const widenStartTimeSelectOptions = widenUiBounds.showStartEditor
    ? buildWidenTimeSelectOptions(
        widenUiBounds.startInputMin,
        widenUiBounds.startInputMax,
        WIDEN_TIME_SELECT_STEP_MINUTES
      )
    : [];
  const widenEndTimeSelectOptions = widenUiBounds.showEndEditor
    ? buildWidenTimeSelectOptions(widenUiBounds.endInputMin, widenUiBounds.endInputMax, WIDEN_TIME_SELECT_STEP_MINUTES)
    : [];
  const widenSelectionState = getWidenSelectionState({
    widenUiBounds,
    windowsQueryEnabled,
    widenStartTime,
    widenEndTime,
    widenStartTimeSelectOptions,
    widenEndTimeSelectOptions,
    deliveryWindowDates: availableWindows,
    bookingTimeZone: booking.address.timezone.value,
    widenDateForApi,
    widenFeasibleDiscountTiers
  });

  const {
    flatWidenWindows,
    matchedDeliveryWindowId,
    widenEarnedDiscountLabel,
    widenExpansionMinutes,
    widenNextTierNudgeDescription,
    widenWindowMatch,
    resolvedWidenEndTime,
    resolvedWidenStartTime
  } = widenSelectionState;
  const earliestTime = widenUiBounds.startInputMin;
  const latestTime = widenUiBounds.endInputMax;
  const earliestLabel = t`Earliest: ${earliestTime}`;
  const latestLabel = t`Latest: ${latestTime}`;
  const widenDirectionHint = getWidenDirectionCustomerHint(offer.widenDirection);
  const widenDirectionHintFollowUp = t`Widen your time window as much as you reasonably can — it gives us more flexibility when we plan.`;

  const handleAcceptWiden = () => {
    if (onRequireLogin('widen')) {
      return;
    }

    if (matchedDeliveryWindowId == null || widenDateForApi.length === 0) {
      return;
    }

    if (widenSelectionState.useCustomWidenTimes && !widenSelectionState.customWidenTimesValid) {
      noddiToast.error(t`End time must be after start time.`);
      return;
    }

    const submitStartTime =
      resolvedWidenStartTime !== ''
        ? clampHmToRange(resolvedWidenStartTime, widenUiBounds.startInputMin, widenUiBounds.startInputMax)
        : resolvedWidenStartTime;
    const submitEndTime =
      resolvedWidenEndTime !== ''
        ? clampHmToRange(resolvedWidenEndTime, widenUiBounds.endInputMin, widenUiBounds.endInputMax)
        : resolvedWidenEndTime;

    acceptWiden({
      offerUid: offer.id,
      date: widenDateForApi,
      deliveryWindowId: matchedDeliveryWindowId,
      ...(widenSelectionState.useCustomWidenTimes
        ? {
            customStartTime: localWallTimeOnServiceDateToUtcIso(
              widenDateForApi,
              submitStartTime,
              booking.address.timezone.value
            ),
            customEndTime: localWallTimeOnServiceDateToUtcIso(
              widenDateForApi,
              submitEndTime,
              booking.address.timezone.value
            )
          }
        : {})
    });
  };

  return (
    <div className='flex flex-col gap-4'>
      <NoddiButton variant='ghost' startIcon='ArrowLeft' className='-ml-4' onClick={onBack}>
        <Trans>Back to options</Trans>
      </NoddiButton>

      {availableWindowsError != null && <ApiErrorMessageInternal error={availableWindowsError} />}

      {!widenUiBounds.staffLimitsFeasibleWithBooking && (
        <NoddiInfoBanner
          variant='error'
          texts={[{ value: t`Your time window already covers the full range — nothing to adjust here.` }]}
        />
      )}

      <NoddiBasicCard>
        <div className='flex flex-col gap-4'>
          {widenDirectionHint != null && (
            <div className='flex min-w-0 flex-col gap-1 border-b border-border pb-4'>
              <p className='text-sm leading-snug font-semibold text-foreground'>{widenDirectionHint}</p>
              <p className='text-sm leading-snug text-foreground'>{widenDirectionHintFollowUp}</p>
            </div>
          )}

          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <div className='flex flex-col gap-1.5'>
              <TimeInputSection
                currentTime={widenUiBounds.currentStart}
                fallbackLabel={earliestLabel}
                inputLabel={widenUiBounds.showStartEditor ? t`I'm available from` : t`Start`}
                isDisabled={isAvailableWindowsLoading}
                isEditorVisible={widenUiBounds.showStartEditor}
                onChange={setWidenStartTime}
                options={widenStartTimeSelectOptions}
                value={resolvedWidenStartTime}
              />
            </div>

            <div className='flex flex-col gap-1.5'>
              <TimeInputSection
                currentTime={widenUiBounds.currentEnd}
                fallbackLabel={latestLabel}
                inputLabel={widenUiBounds.showEndEditor ? t`I'm available until` : t`End`}
                isDisabled={isAvailableWindowsLoading}
                isEditorVisible={widenUiBounds.showEndEditor}
                onChange={setWidenEndTime}
                options={widenEndTimeSelectOptions}
                value={resolvedWidenEndTime}
              />
            </div>
          </div>

          {widenExpansionMinutes > 0 && (
            <>
              <div className='border-t border-border' />
              <div className='flex items-baseline justify-between'>
                <p className='text-sm'>
                  <Trans>Extra flexibility</Trans>
                </p>
                <p className='font-semibold tabular-nums'>{formatWholeMinutesAsTimeString(widenExpansionMinutes)}</p>
              </div>
              {widenEarnedDiscountLabel != null && (
                <p className='text-sm font-medium text-successDark'>
                  <Trans>Your discount: {widenEarnedDiscountLabel}</Trans>
                </p>
              )}
            </>
          )}

          {widenNextTierNudgeDescription != null && (
            <p className='text-xs text-muted-foreground'>{widenNextTierNudgeDescription}</p>
          )}
        </div>
      </NoddiBasicCard>

      {widenFeasibleDiscountTiers.length > 0 && <DiscountTiersList tiers={widenFeasibleDiscountTiers} />}

      {isAvailableWindowsLoading && (
        <p className='text-xs text-muted-foreground'>
          <Trans>Loading available time slots…</Trans>
        </p>
      )}

      {!isAvailableWindowsLoading && flatWidenWindows.length === 0 && windowsQueryEnabled ? (
        <NoddiInfoBanner
          variant='warning'
          texts={[{ value: t`No time slots available. Try again later or pick another day.` }]}
        />
      ) : null}

      {resolvedWidenStartTime !== '' &&
        resolvedWidenEndTime !== '' &&
        !isAvailableWindowsLoading &&
        flatWidenWindows.length > 0 &&
        widenWindowMatch == null && (
          <NoddiInfoBanner
            variant='warning'
            texts={[{ value: t`No available slot matches those times. Try widening a bit more.` }]}
          />
        )}

      <NoddiButton
        className='w-full'
        loading={isAcceptPending}
        disabled={
          matchedDeliveryWindowId == null ||
          widenDateForApi.length === 0 ||
          isAvailableWindowsLoading ||
          !widenUiBounds.staffLimitsFeasibleWithBooking
        }
        onClick={handleAcceptWiden}
      >
        <Trans>Confirm new time window</Trans>
      </NoddiButton>
    </div>
  );
};
